import React, { useEffect, useState } from 'react';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { trackInsightsException } from './utils/telemetry/appinsights';
import { InAppBrowser } from '@capgo/capacitor-inappbrowser';
import { useRouter } from 'next/navigation';
import { getDeeplinkPathFromUrl } from './utils/utils';
import { dispatchAppUrlHandlers } from './appUrlHandlers';
// Imported for their module-level registerAppUrlHandler side effects.
import './components/epassi/epassiMobileResult';
import './components/paymentRedirectHandler/appPaymentReturnResult';

const AppUrlListener: React.FC = () => {
  const [listenerHandle, setListenerHandle] = useState<PluginListenerHandle | null>(null);

  const router = useRouter();

  const closeInAppBrowser = async (): Promise<void> => {
    await InAppBrowser.removeAllListeners();
    await InAppBrowser.close();
  };

  useEffect(() => {
    // Only do Capacitor stuff if we're on a native platform
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    (async () => {
      try {
        // Remove previous listener, if any
        if (listenerHandle !== null) {
          await listenerHandle.remove();
          setListenerHandle(null);
        }

        const handle = await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
          // Provider-specific custom-scheme returns (e.g. Epassi's iOS wallet-token
          // return) are claimed by a registered handler — see appUrlHandlers.ts —
          // so this shared listener doesn't grow a hardcoded branch per provider.
          if (dispatchAppUrlHandlers(event.url)) {
            return;
          }

          // Custom-scheme URLs (Facebook/Google auth callbacks etc.) are handled by their
          // own plugins and must not be pushed into the router.
          if (!event.url.startsWith('https://')) {
            return;
          }

          // Example url: https://kotipizzadev.fi/foo/bar -> /foo/bar
          const path = getDeeplinkPathFromUrl(event.url);

          if (path) {
            router.push(path);
            closeInAppBrowser(); // InAppBrowser should be closed if the app gets appUrlOpenEvent. For example card payment and 3DS verification in a bank ID app that redirects back to Kotipizza App
          }
          // If no match, do nothing - let regular routing logic take over
        });

        setListenerHandle(handle);
      } catch (e: unknown) {
        trackInsightsException(
          'Error while initializing Capacitor -> adding appUrlOpen listener',
          e
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default AppUrlListener;
